import React from 'react'
import { Box, List, ListItem, Stack, Typography } from '@mui/material'
import { FormattedMessage } from 'react-intl'
import styled from '@emotion/styled'
import Amount from '../Common/Amount'

type MetadataBlockType = {
  title: string
  data: string | string[] | number | JSX.Element[]
  isSubscription?: boolean
}

const StyledTypography = styled(Typography)({
  color: '#F5F5F5'
})

const EducationMetadataBlock = ({ title, data, isSubscription }: MetadataBlockType) => {
  return (
    <Stack sx={{ flexDirection: 'row', gap: 1, width: '100%' }}>
      <Typography>
        <FormattedMessage id={title} />:
      </Typography>
      <ContentRenderer title={title} data={data} />
      {isSubscription && (
        <Box sx={{ ml: -0.6 }}>
          /<FormattedMessage id='common.year' />
        </Box>
      )}
    </Stack>
  )
}

const ContentRenderer = ({ title, data }: MetadataBlockType) => {
  const renderSkillLevelText = (data: string[]) => {
    switch (data[0].trim()) {
      case 'Beginner':
        return (
          <StyledTypography>
            <FormattedMessage id='learning.level.beginner' />
          </StyledTypography>
        )
      case 'Intermediate':
        return (
          <StyledTypography>
            <FormattedMessage id='learning.level.intermediate' />
          </StyledTypography>
        )
      case 'Beginner to Intermediate':
        return (
          <StyledTypography>
            <FormattedMessage id='learning.level.beginnerToIntermediate' />
          </StyledTypography>
        )
      case 'Advance':
        return (
          <StyledTypography>
            <FormattedMessage id='learning.level.advanced' />
          </StyledTypography>
        )
      default:
        return <StyledTypography>{data}</StyledTypography>
    }
  }

  if (!data) {
    return (
      <StyledTypography>
        <FormattedMessage id='education.noDataFound' />
      </StyledTypography>
    )
  }

  switch (title) {
    case 'education.metadata.dates':
      return <DatesList data={data as string[]} />
    case 'education.metadata.price':
      return <PriceDisplay data={data as number} />
    case 'education.metadata.skillLevel':
      return renderSkillLevelText(data as string[])
    default:
      return <DefaultDisplay data={data as string} />
  }
}

const DatesList = ({ data }: { data: string[] }) =>
  !data.length ? (
    <StyledTypography>
      <FormattedMessage id='common.comingSoon' />
    </StyledTypography>
  ) : (
    <List
      sx={{
        margin: 0,
        padding: 0,
        listStyleType: 'disc',
        pl: 2
      }}
    >
      {data.map((item, index) => (
        <ListItem key={index} sx={{ padding: 0, marginLeft: 1, display: 'list-item', color: 'white' }}>
          {item}
        </ListItem>
      ))}
    </List>
  )

const PriceDisplay = ({ data }: { data: number }) => (
  <StyledTypography>
    <Amount value={data / 100} />
  </StyledTypography>
)

const DefaultDisplay = ({ data }: { data: string | number }) => <StyledTypography>{data}</StyledTypography>

export default EducationMetadataBlock
